import { cardSurfaceClass, cardTitleTextClass, secondaryBtnClass } from "@/components/common/surfaceClasses";
import type { Dispatch, SetStateAction } from "react";
import type { ContactResult } from "../../../lib/contacts/types";
import { type LocationResult } from "../../../lib/contacts/router";
import type { TrainingAnswer } from "../../../lib/training/types";
import { speak } from "../../../lib/speech/speak";
import { useVoiceSettings } from "@/hooks/useVoiceSettings";
import {
  classifyPurposeFromQuery,
  trainingAskResultBadgeLabel,
  type GuidanceSnapshot,
} from "./trainingPanelGuidance";
import { TrainingAskFeedbackActions } from "./TrainingAskFeedbackActions";
import { TrainingAskSourcesContacts } from "./TrainingAskSourcesContacts";

type TrainingAskResultCardProps = {
  /** When false (default), hide structured-pricing diagnostics (quarantined for non-platform roles). */
  showPricingAsk?: boolean;
  result: TrainingAnswer;
  answerMode: "kb" | "contacts" | "location";
  contacts: ContactResult[];
  needsClarification: boolean;
  activeGuidance: GuidanceSnapshot | null;
  scopedOrganizationId: string;
  resultQuery: string;
  lastQuery: string;
  query: string;
  appVersion: string;
  webhookUrl?: string;
  locationId?: string;
  locationName?: string;
  salespersonName?: string;
  salespersonFirstName?: string;
  askThreadId: string | null;
  askThreadTurnsCount: number;
  nextAskKind: "new" | "followup";
  canEscalateKbThread: boolean;
  kbTestingModeEnabled: boolean;
  proposeOpen: boolean;
  setProposeOpen: Dispatch<SetStateAction<boolean>>;
  proposeTitle: string;
  setProposeTitle: Dispatch<SetStateAction<string>>;
  proposeBody: string;
  setProposeBody: Dispatch<SetStateAction<string>>;
  proposeSubmitting: boolean;
  setProposeSubmitting: Dispatch<SetStateAction<boolean>>;
  proposeMessage: { tone: "ok" | "err"; text: string } | null;
  setProposeMessage: Dispatch<SetStateAction<{ tone: "ok" | "err"; text: string } | null>>;
  thumbsDownOpen: boolean;
  setThumbsDownOpen: Dispatch<SetStateAction<boolean>>;
  thumbsDownBusy: boolean;
  setThumbsDownBusy: Dispatch<SetStateAction<boolean>>;
  thumbsDownReason:
    | ""
    | "wrong_answer"
    | "missing_detail"
    | "outdated_policy"
    | "slang_uncommon_phrasing"
    | "wrong_contacts";
  setThumbsDownReason: Dispatch<
    SetStateAction<
      "" | "wrong_answer" | "missing_detail" | "outdated_policy" | "slang_uncommon_phrasing" | "wrong_contacts"
    >
  >;
  thumbsDownNotes: string;
  setThumbsDownNotes: Dispatch<SetStateAction<string>>;
  thumbsDownExpectedAnswer: string;
  setThumbsDownExpectedAnswer: Dispatch<SetStateAction<string>>;
  thumbsDownImpact: string;
  setThumbsDownImpact: Dispatch<SetStateAction<string>>;
  thumbsDownMessage: { tone: "ok" | "err"; text: string } | null;
  setThumbsDownMessage: Dispatch<SetStateAction<{ tone: "ok" | "err"; text: string } | null>>;
  locationCards: LocationResult[];
  locationError: string | null;
  contactsError: string | null;
  contactsWhy: "policy_support" | "direct" | "";
  onContactTagClick?: (tag: string) => void;
};

export function TrainingAskResultCard(props: TrainingAskResultCardProps) {
  const showPricingAsk = props.showPricingAsk === true;
  const pricingLike =
    props.activeGuidance?.lane === "pricing" ||
    classifyPurposeFromQuery(props.resultQuery || props.query) === "pricing";
  const showNoPricingAccessFooter = props.answerMode === "kb" && !showPricingAsk && pricingLike;
  const sourceModels = props.result.pricingDiagnostics?.sourceModels ?? [];
  const hasPricingDiagnostics = showPricingAsk && props.answerMode === "kb" && sourceModels.length > 0;
  const pricingSkipReason = props.result.pricingDiagnostics?.skipReason ?? null;
  const showPricingDevDiagnostics =
    showPricingAsk && props.answerMode === "kb" && props.activeGuidance?.lane === "pricing" && pricingSkipReason;

  const { voiceEnabled } = useVoiceSettings();
  const renderedAnswerText =
    props.answerMode === "contacts" && props.contacts.length > 0 && props.needsClarification
      ? "I found contacts that can help with your question. If you were asking for policy details instead, try a more specific policy question like: \u201CWhat sales paperwork is required for a cash sale of a custom shed?\u201D"
      : props.result.answer;
  // Per dev-sync note 25a655a1 — give users an explicit "Read aloud" affordance
  // on every answer so they can replay the response (or trigger it manually
  // when speakMode = "help" disables auto-play). Voice-off hides the button
  // entirely since speak() short-circuits on !voiceEnabled and would feel
  // broken; users can toggle voice back on from the rail Settings popover.
  const canPlayAnswer = voiceEnabled && typeof renderedAnswerText === "string" && renderedAnswerText.trim().length > 0;
  const handleReadAloud = () => {
    if (!canPlayAnswer) return;
    speak(renderedAnswerText, { profile: "manager" });
  };

  return (
    <div className={`mt-4 ${cardSurfaceClass} p-4`}>
      <div className="mb-2">
        <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold border border-gray-200 bg-gray-50 text-gray-700 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-300">
          {trainingAskResultBadgeLabel(props.answerMode, props.activeGuidance?.lane)}
        </span>
      </div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Answer</div>
        {canPlayAnswer ? (
          <button
            type="button"
            onClick={handleReadAloud}
            className={`inline-flex items-center gap-1 rounded-full ${secondaryBtnClass} px-2.5 py-1 text-[11px] font-medium`}
            aria-label="Read this answer aloud"
            title="Read aloud"
          >
            <span aria-hidden="true">🔊</span>
            <span>Read aloud</span>
          </button>
        ) : null}
      </div>
      <div className={`text-sm whitespace-pre-wrap ${cardTitleTextClass}`}>{renderedAnswerText}</div>
      {props.activeGuidance &&
      props.activeGuidance.lane !== "contacts" &&
      (showPricingAsk || props.activeGuidance.lane !== "pricing") &&
      props.answerMode !== "contacts" &&
      props.answerMode !== "location" ? (
        <div className="mt-2 text-xs text-gray-600">
          Active lane <strong>{props.activeGuidance.lane}</strong>. The assistant answered based on question purpose{" "}
          <strong>{props.activeGuidance.purpose}</strong> and product type <strong>{props.activeGuidance.product}</strong>.
          Open Knowledge Base Guidance Filters to alter that selection.
        </div>
      ) : null}
      {hasPricingDiagnostics ? (
        <div className="mt-2 text-xs text-gray-600">
          Pricing source model: <strong>{sourceModels.join(", ")}</strong>
          {props.result.pricingDiagnostics?.hasActivePricingFacts ? " (active facts)" : " (no active facts)"}.
        </div>
      ) : null}
      {showPricingDevDiagnostics ? (
        <div className="mt-1 text-[11px] text-amber-700">
          Pricing debug: skip reason <strong>{pricingSkipReason}</strong>.
        </div>
      ) : null}
      {props.needsClarification && <div className="mt-2 text-xs text-amber-700">Needs clarification.</div>}

      <TrainingAskFeedbackActions
        result={props.result}
        answerMode={props.answerMode}
        scopedOrganizationId={props.scopedOrganizationId}
        resultQuery={props.resultQuery}
        lastQuery={props.lastQuery}
        query={props.query}
        salespersonName={props.salespersonName}
        salespersonFirstName={props.salespersonFirstName}
        appVersion={props.appVersion}
        webhookUrl={props.webhookUrl}
        locationId={props.locationId}
        locationName={props.locationName}
        askThreadId={props.askThreadId}
        askThreadTurnsCount={props.askThreadTurnsCount}
        nextAskKind={props.nextAskKind}
        canEscalateKbThread={props.canEscalateKbThread}
        activeGuidance={props.activeGuidance}
        kbTestingModeEnabled={props.kbTestingModeEnabled}
        proposeOpen={props.proposeOpen}
        setProposeOpen={props.setProposeOpen}
        proposeTitle={props.proposeTitle}
        setProposeTitle={props.setProposeTitle}
        proposeBody={props.proposeBody}
        setProposeBody={props.setProposeBody}
        proposeSubmitting={props.proposeSubmitting}
        setProposeSubmitting={props.setProposeSubmitting}
        proposeMessage={props.proposeMessage}
        setProposeMessage={props.setProposeMessage}
        thumbsDownOpen={props.thumbsDownOpen}
        setThumbsDownOpen={props.setThumbsDownOpen}
        thumbsDownBusy={props.thumbsDownBusy}
        setThumbsDownBusy={props.setThumbsDownBusy}
        thumbsDownReason={props.thumbsDownReason}
        setThumbsDownReason={props.setThumbsDownReason}
        thumbsDownNotes={props.thumbsDownNotes}
        setThumbsDownNotes={props.setThumbsDownNotes}
        thumbsDownExpectedAnswer={props.thumbsDownExpectedAnswer}
        setThumbsDownExpectedAnswer={props.setThumbsDownExpectedAnswer}
        thumbsDownImpact={props.thumbsDownImpact}
        setThumbsDownImpact={props.setThumbsDownImpact}
        thumbsDownMessage={props.thumbsDownMessage}
        setThumbsDownMessage={props.setThumbsDownMessage}
      />

      <TrainingAskSourcesContacts
        result={props.result}
        answerMode={props.answerMode}
        locationCards={props.locationCards}
        locationError={props.locationError}
        contacts={props.contacts}
        contactsError={props.contactsError}
        contactsWhy={props.contactsWhy}
        scopedOrganizationId={props.scopedOrganizationId}
        resultQuery={props.resultQuery}
        lastQuery={props.lastQuery}
        query={props.query}
        onContactTagClick={props.onContactTagClick}
      />
      {showNoPricingAccessFooter ? (
        <p className="mt-4 text-xs text-gray-500 border-t border-gray-100 pt-3 dark:border-gray-700 dark:text-gray-400">
          This app does not have access to pricing estimations. Use your company pricing tools for quoting.
        </p>
      ) : null}
    </div>
  );
}
